import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, Sparkles, CalendarDays, Rocket, Lightbulb } from "lucide-react";
import { useAuth } from "../../services/AuthContext.js";
import { AccountSpecificMessage } from "../../components/EnrollButton.js";
import "./news.css";

const baseAPI_URL = process.env.REACT_APP_BACKEND_API_BASE_ENDPOINT;

const HIGHLIGHT = {
  text:
    "A área social do Domínio Elétrico está no ar: acompanhe o feed de publicações, veja a rede de pessoas, converse por chat (direto ou em grupo) e personalize a sua página de perfil.",
  linkTo: "/feed",
  linkLabel: "Abrir o feed",
};

const UPDATES = [
  { date: "27/07/2026", text: "Posts, feed, chats e rede e melhorias visuais na parte interna do site" },
  { date: "11/06/2026", text: "Correções visuais nas páginas públicas" },
  { date: "08/06/2026", text: "Informações e gerenciamento de assinaturas e pagamentos à página de 'Sua Conta'" },
  { date: "18/05/2026", text: "Melhorias visuais nas páginas públicas" },
  { date: "18/05/2026", text: "Nova versão do fluxo de criação de conta premium" },
  { date: "18/05/2026", text: "Melhoria do fluxo de criação de conta gratuita" },
  { date: "21/04/2026", text: "Correção de problemas críticos" },
  { date: "21/04/2026", text: "Sino de notificações" },
  { date: "21/04/2026", text: "Seção de comentários nas aulas" },
  { date: "08/04/2026", text: "Implementação de página pública de perfil" },
  { date: "08/04/2026", text: "Implementação de página de edição de perfil" },
  { date: "04/04/2026", text: "Criação de página de erro 404" },
  { date: "28/03/2026", text: "Atualizações de segurança" },
  { date: "17/03/2026", text: "Melhoria do tratamento de erro da página de resetar senha" },
  { date: "13/12/2025", text: "Melhoria visual nas páginas de login, cadastro, resetar senha e verificação" },
  { date: "13/12/2025", text: "Barra de progresso mostrando porcentagem concluída do curso na aba da aula" },
  { date: "11/12/2025", text: "Botões de navegação para ir para aula anterior e próxima aula" },
  { date: "11/12/2025", text: "Correção de problema que impedia desmarcar aula como salva" },
  { date: "09/12/2025", text: "Recurso para mostrar duração das aulas, seções e do curso inteiro" },
  { date: "08/12/2025", text: "Melhoria visual na página de aula" },
  { date: "05/12/2025", text: "Melhorias visuais na página de Dashboard, na listagem de cursos e nas páginas de curso" },
  { date: "05/12/2025", text: "Correção de problema que impedia usuários logados de se inscreverem no curso" },
  { date: "04/12/2025", text: "Funcionalidade para marcar aulas como concluídas" },
  { date: "04/12/2025", text: "Correção de problemas de performance na página de chats" },
  { date: "04/12/2025", text: "Primeira versão da página 'Sua conta'" },
];

const PLANNED = [
  "Recurso para mostrar porcentagem concluída do curso em outras partes do site",
  "Melhorias nas notificações",
  "Melhorias nas páginas públicas de curso"
];

const News = () => {
  const { user, getIdToken } = useAuth();
  const [accountType, setAccountType] = useState("free");

  useEffect(() => {
    if (!user) return;

    let isMounted = true;
    const fetchAccountType = async () => {
      try {
        const token = await getIdToken();
        const res = await fetch(`${baseAPI_URL}/users/get-user-data`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (isMounted) setAccountType(data.account_type);
      } catch (err) {
        console.error("Failed to fetch user data", err);
      }
    };

    fetchAccountType();
    return () => {
      isMounted = false;
    };
  }, [user, getIdToken]);

  return (
    <section className="news-page">
      <div className="news-inner">
        <header className="news-header">
          <div className="news-header-text">
            <h1 className="news-title">
              <MessageSquare size={28} className="news-title-icon" />
              Novidades
            </h1>
            <p className="news-subtitle">
              Acompanhe as atualizações e o que está por vir no Domínio Elétrico.
            </p>
          </div>
          <AccountSpecificMessage account_type={accountType} />
        </header>

        <div className="news-panel news-highlight">
          <div className="news-panel-heading">
            <Sparkles size={18} />
            <h2 className="news-panel-title">Novidade do site</h2>
          </div>
          <p className="news-panel-text">{HIGHLIGHT.text}</p>
          <Link to={HIGHLIGHT.linkTo} className="news-panel-link">
            {HIGHLIGHT.linkLabel}
          </Link>
        </div>

        <div className="news-panel">
          <div className="news-panel-heading">
            <CalendarDays size={18} />
            <h2 className="news-panel-title">Últimas atualizações</h2>
          </div>
          <ul className="news-timeline">
            {UPDATES.map((item, index) => (
              <li key={`${item.date}-${index}`} className="news-timeline-item">
                <span className="news-timeline-date">{item.date}</span>
                <span className="news-timeline-text">{item.text}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="news-panel">
          <div className="news-panel-heading">
            <Rocket size={18} />
            <h2 className="news-panel-title">Planejado para o futuro</h2>
          </div>
          <p className="news-panel-note">
            Itens planejados — não necessariamente nesta sequência.
          </p>
          <ul className="news-planned-list">
            {PLANNED.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>

        <div className="news-panel news-suggestions">
          <div className="news-panel-heading">
            <Lightbulb size={18} />
            <h2 className="news-panel-title">Sugestões</h2>
          </div>
          <p className="news-panel-text">
            Se tiver sugestões de novos recursos ou funcionalidades que você acha
            interessantes para o site, mande mensagem para nós por{" "}
            <Link to="/chats">mensagem direta</Link> aqui no site mesmo.
          </p>
        </div>
      </div>
    </section>
  );
};

export default News;
